import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  runTransaction,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { useEffect, useState } from "react";
import { db } from "@/firebase/app";
import { apiRequest } from "./functionsClient";
import {
  overlayConfigFirestoreData,
  parseOverlayConfig,
  type OverlayConfig,
} from "@/models/overlayConfig";
import {
  parseCustomSkinReference,
  requireStoredSkinSpec,
  skinSnapshotKey,
  type LeagueOverlaySkin,
  type OverlayCustomSkinReference,
  type OverlaySkinRevision,
  type OverlaySkinSnapshots,
} from "@/models/overlaySkin";
export function useLeagueOverlaySkins(leagueID: string | null) {
  const [state, setState] = useState<{
    leagueID: string | null;
    skins: LeagueOverlaySkin[];
    error: string | null;
    loading: boolean;
  }>({ leagueID: null, skins: [], error: null, loading: false });
  useEffect(() => {
    if (!leagueID) return;
    setState({ leagueID, skins: [], error: null, loading: true });
    return onSnapshot(
      collection(db, "leagues", leagueID, "overlaySkins"),
      (snapshot) => {
        const skins = snapshot.docs
          .map(
            (entry) => ({ ...entry.data(), id: entry.id }) as LeagueOverlaySkin,
          )
          .filter(
            (skin) =>
              typeof skin.name === "string" &&
              typeof skin.latestRevisionID === "string",
          );
        setState({
          leagueID,
          skins: skins.sort((a, b) => a.name.localeCompare(b.name)),
          error: null,
          loading: false,
        });
      },
      (error) =>
        setState({ leagueID, skins: [], error: error.message, loading: false }),
    );
  }, [leagueID]);
  return state.leagueID === leagueID
    ? state
    : { leagueID, skins: [], error: null, loading: !!leagueID };
}
export async function listLeagueSkins(
  leagueID: string,
): Promise<LeagueOverlaySkin[]> {
  const snapshot = await getDocs(
    collection(db, "leagues", leagueID, "overlaySkins"),
  );
  return snapshot.docs.map(
    (entry) => ({ ...entry.data(), id: entry.id }) as LeagueOverlaySkin,
  );
}
export async function readLeagueSkin(
  leagueID: string,
  skinID: string,
): Promise<LeagueOverlaySkin> {
  const snapshot = await getDoc(
    doc(db, "leagues", leagueID, "overlaySkins", skinID),
  );
  if (!snapshot.exists()) throw new Error("Skin not found in this league");
  return { ...snapshot.data(), id: snapshot.id } as LeagueOverlaySkin;
}
export async function listSkinRevisions(
  leagueID: string,
  skinID: string,
): Promise<OverlaySkinRevision[]> {
  const snapshot = await getDocs(
    collection(db, "leagues", leagueID, "overlaySkins", skinID, "revisions"),
  );
  return snapshot.docs.map(
    (entry) =>
      ({
        ...entry.data(),
        id: entry.id,
        spec: requireStoredSkinSpec(entry.data().spec),
      }) as OverlaySkinRevision,
  );
}
export async function readSkinRevision(
  leagueID: string,
  reference: OverlayCustomSkinReference,
): Promise<OverlaySkinRevision> {
  if (!parseCustomSkinReference(reference))
    throw new Error("Invalid skin reference");
  const snapshot = await getDoc(
    doc(
      db,
      "leagues",
      leagueID,
      "overlaySkins",
      reference.skinID,
      "revisions",
      reference.revisionID,
    ),
  );
  if (!snapshot.exists())
    throw new Error("Skin revision not found in this league");
  return {
    ...snapshot.data(),
    id: snapshot.id,
    spec: requireStoredSkinSpec(snapshot.data().spec),
  } as OverlaySkinRevision;
}
export function mutateLeagueSkin(
  leagueID: string,
  body: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  return apiRequest(`/leagues/${encodeURIComponent(leagueID)}/overlay-skins`, {
    body,
  });
}
export function referencedCustomSkins(
  config: OverlayConfig,
): OverlayCustomSkinReference[] {
  const all = [
    config.design.customSkin,
    ...Object.values(config.elements).flatMap((instances) =>
      instances.map(
        (instance: { customSkin?: OverlayCustomSkinReference }) =>
          instance.customSkin,
      ),
    ),
  ];
  return [
    ...new Map(
      all
        .filter((v): v is OverlayCustomSkinReference => !!v)
        .map((v) => [skinSnapshotKey(v), v]),
    ).values(),
  ];
}
export async function attachOverlaySkinSnapshots(
  config: OverlayConfig,
  sourceOverlayID?: string,
): Promise<void> {
  const references = referencedCustomSkins(config);
  if (!references.length) return;
  await mutateLeagueSkin(config.leagueID, {
    operation: "attach",
    operationID: crypto.randomUUID(),
    overlayID: config.id,
    references,
    ...(sourceOverlayID ? { sourceOverlayID } : {}),
  });
}
/** Must use current raw data: unsaved preview snapshots are never authoritative. */
export function preserveSkinServerFields(
  saved: Record<string, unknown>,
  current: Record<string, unknown>,
) {
  if (current.skinContractVersion === 1) {
    saved.skinContractVersion = 1;
    saved.skinSnapshots = current.skinSnapshots ?? {};
  } else {
    delete saved.skinContractVersion;
    delete saved.skinSnapshots;
  }
  saved.skinContractWrittenAt = serverTimestamp();
}
/** Template creation installs validated server snapshots before any custom selection is made public. */
export async function createOverlayWithSkins(
  config: OverlayConfig,
  sourceOverlayID?: string,
) {
  const reference = doc(db, "overlayConfigs", config.id);
  if (!referencedCustomSkins(config).length) {
    await setDoc(
      reference,
      overlayConfigFirestoreData({
        ...config,
        skinContractVersion: undefined,
        skinSnapshots: undefined,
      }),
    );
    return;
  }
  const initial = structuredClone(config);
  delete initial.design.customSkin;
  for (const instances of Object.values(initial.elements))
    for (const instance of instances)
      delete (instance as { customSkin?: unknown }).customSkin;
  delete initial.skinContractVersion;
  delete initial.skinSnapshots;
  await setDoc(reference, overlayConfigFirestoreData(initial));
  await attachOverlaySkinSnapshots(config, sourceOverlayID);
  await writeOverExistingOverlay(reference, () => config);
}
/**
 * Replace an existing overlay's content in place — the update twin of
 * createOverlayWithSkins. The server attaches any custom-skin snapshots the
 * new content references (copying pinned revisions from `sourceOverlayID`)
 * BEFORE the client write: the update rule only accepts references already
 * present in the stored snapshots. `build` runs against the transaction's
 * fresh read so a concurrent live write (course, focus) is never clobbered.
 */
export async function replaceOverlayWithSkins(
  overlayID: string,
  sourceOverlayID: string | undefined,
  build: (current: OverlayConfig) => OverlayConfig,
) {
  const reference = doc(db, "overlayConfigs", overlayID);
  const snapshot = await getDoc(reference);
  const current = snapshot.exists() ? parseOverlayConfig(snapshot.id, snapshot.data()) : null;
  if (!current) throw new Error("Overlay unavailable");
  await attachOverlaySkinSnapshots(build(current), sourceOverlayID);
  await writeOverExistingOverlay(reference, (fresh) => ({ ...build(fresh), updatedAt: new Date() }));
}
/** Full-doc write over a live overlay; skin fields always come from the stored doc. */
async function writeOverExistingOverlay(
  reference: ReturnType<typeof doc>,
  build: (current: OverlayConfig) => OverlayConfig,
) {
  await runTransaction(db, async (transaction) => {
    const current = await transaction.get(reference);
    const data = current.exists() ? current.data() : undefined;
    const parsed = data ? parseOverlayConfig(current.id, data) : null;
    if (!data || !parsed) throw new Error("Overlay unavailable");
    const saved = overlayConfigFirestoreData(build(parsed));
    preserveSkinServerFields(saved, data);
    transaction.set(reference, saved);
  });
}
export type SkinCatalogue = ReturnType<typeof useLeagueOverlaySkins>;
export type { OverlaySkinSnapshots };
